import { Box, Button, List, MenuItem } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';

const Styled = {
    Sidebar: styled(Box)(({ theme }) => ({
        width: 280,
        height: '100dvh',
        flexShrink: 0,
        position: 'sticky',
        top: 0,
        padding: theme.spacing(1.5, 0, 1.5, 1.5),
        transition: theme.transitions.create('width', {
            duration: theme.transitions.duration.short,
        }),

        [theme.breakpoints.between('md', 'lg')]: {
            width: 244,
        },
        [theme.breakpoints.down('md')]: {
            display: 'none',
        },

        '&.collapse': {
            width: 92,
            '.MuiButtonBase-root': {
                paddingTop: '11px',
                paddingBottom: '11px',
                paddingLeft: 0,
                paddingRight: 0,
                justifyContent: 'center',
                '.MuiListItemText-root': {
                    display: 'none',
                },
                '.MuiListItemIcon-root': {
                    minWidth: 'auto',
                    margin: 0,
                },
            },
            '.user-area-button': {
                width: 50,
                minWidth: 50,
                height: 50,
                minHeight: 50,
                marginInline: 'auto',
                padding: 4,
                justifyContent: 'center',
                borderRadius: 8,
                backgroundColor: alpha(
                    theme.palette.background.extraLight ??
                        theme.palette.background.paper,
                    0.88,
                ),
                border: `1px solid ${theme.palette.border.light}`,
                boxShadow: `0 8px 20px ${alpha(
                    theme.palette.common.black,
                    theme.palette.mode === 'dark' ? 0.2 : 0.07,
                )}`,
                '.description, .MuiButton-endIcon, .MuiTouchRipple-root': {
                    display: 'none',
                },
                '.MuiAvatar-root': {
                    width: 38,
                    height: 38,
                },
                '&:hover, &.open': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.09),
                    borderColor: alpha(theme.palette.primary.main, 0.28),
                },
            },
        },
    })),

    SidebarContent: styled(Box)(({ theme }) => ({
        position: 'relative',
        height: '100%',
        padding: theme.spacing(1.5),
        overflow: 'visible',
        boxSizing: 'border-box',
        backgroundColor: alpha(
            theme.palette.background.paper,
            theme.palette.mode === 'light' ? 0.94 : 0.9,
        ),
        backgroundImage:
            theme.palette.mode === 'light'
                ? `linear-gradient(180deg, ${alpha(theme.palette.common.white, 0.8)} 0%, transparent 24%)`
                : `linear-gradient(180deg, ${alpha(theme.palette.common.white, 0.035)} 0%, transparent 22%)`,
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        border: `1px solid ${theme.palette.border.light}`,
        borderRadius: 20,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        boxShadow:
            theme.palette.mode === 'light'
                ? `0 16px 44px ${alpha('#172033', 0.065)}`
                : `0 16px 44px ${alpha('#000000', 0.28)}`,
    })),

    SidebarContainer: styled(Box)(() => ({
        position: 'relative',
        width: '100%',
    })),

    CollapseButton: styled(Box)(({ theme }) => ({
        width: 30,
        height: 30,
        borderRadius: 10,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'absolute',
        insetInlineEnd: '-15px',
        top: '22px',
        border: `1px solid ${theme.palette.border.main}`,
        backgroundColor: alpha(theme.palette.background.paper, 0.96),
        color: theme.palette.text.secondary,
        boxShadow: `0 7px 18px ${alpha(
            theme.palette.common.black,
            theme.palette.mode === 'dark' ? 0.28 : 0.11,
        )}`,
        zIndex: theme.zIndex.drawer + 2,
        cursor: 'pointer',
        transition: theme.transitions.create(
            ['background-color', 'border-color', 'color', 'transform'],
            { duration: 140 },
        ),
        '&:hover': {
            color:
                theme.palette.mode === 'dark'
                    ? theme.palette.primary.light
                    : theme.palette.primary.main,
            borderColor: alpha(
                theme.palette.mode === 'dark'
                    ? theme.palette.primary.light
                    : theme.palette.primary.main,
                0.4,
            ),
            backgroundColor: alpha(
                theme.palette.mode === 'dark'
                    ? theme.palette.primary.light
                    : theme.palette.primary.main,
                0.07,
            ),
        },
        '&:active': {
            transform: 'scale(0.95)',
        },
        '& svg': {
            fontSize: 18,
        },
    })),

    MenuList: styled(List)(({ theme }) => ({
        height: '70vh',
        marginTop: theme.spacing(1.75),
        paddingBlock: theme.spacing(0.5),

        '& .MuiListItemButton-root': {
            position: 'relative',
            minHeight: 46,
            marginBottom: theme.spacing(0.55),
            borderRadius: 12,
            padding: theme.spacing(1, 1.05),
            color: theme.palette.text.secondary,

            '.MuiListItemIcon-root': {
                width: 30,
                height: 30,
                minWidth: 30,
                marginInlineEnd: theme.spacing(0.8),
                color: 'inherit',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: 6,
                backgroundColor: alpha(theme.palette.text.secondary, 0.055),
                transition: theme.transitions.create(
                    ['background-color', 'color', 'transform'],
                    { duration: 150 },
                ),
                i: {
                    fontSize: 16,
                },
            },
            '.MuiTypography-root': {
                color: 'inherit',
                marginInlineStart: theme.spacing(0.65),
                fontSize: 13,
                fontWeight: 400,
                textWrap: 'nowrap',
            },
            '&:hover': {
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.action.hover,
                '.MuiListItemIcon-root': {
                    backgroundColor: alpha(
                        theme.palette.mode === 'dark'
                            ? theme.palette.primary.light
                            : theme.palette.primary.main,
                        theme.palette.mode === 'dark' ? 0.065 : 0.07,
                    ),
                    color:
                        theme.palette.mode === 'dark'
                            ? theme.palette.primary.light
                            : theme.palette.primary.main,
                    transform: 'translateY(-1px)',
                },
            },
        },

        '& .active': {
            color:
                theme.palette.mode === 'dark'
                    ? theme.palette.primary.light
                    : theme.palette.primary.main,
            background:
                theme.palette.mode === 'light'
                    ? `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.045)} 0%, ${alpha(theme.palette.primary.main, 0.115)} 100%)`
                    : `linear-gradient(90deg, ${alpha(theme.palette.primary.light, 0.08)} 0%, ${alpha(theme.palette.primary.light, 0.17)} 100%)`,
            boxShadow: `inset 0 0 0 1px ${alpha(
                theme.palette.mode === 'dark'
                    ? theme.palette.primary.light
                    : theme.palette.primary.main,
                theme.palette.mode === 'dark' ? 0.15 : 0.085,
            )}`,

            '.MuiTypography-root, .MuiListItemIcon-root, i': {
                color: 'inherit',
            },
            '.MuiListItemIcon-root': {
                color:
                    theme.palette.mode === 'dark'
                        ? theme.palette.primary.light
                        : theme.palette.primary.main,
                backgroundColor: alpha(
                    theme.palette.mode === 'dark'
                        ? theme.palette.primary.light
                        : theme.palette.primary.main,
                    theme.palette.mode === 'dark' ? 0.13 : 0.12,
                ),
                boxShadow: `0 6px 14px ${alpha(
                    theme.palette.mode === 'dark'
                        ? theme.palette.primary.light
                        : theme.palette.primary.main,
                    0.08,
                )}`,
            },
            '&:after': {
                content: '""',
                position: 'absolute',
                insetInlineEnd: 7,
                width: 3,
                height: 18,
                borderRadius: 99,
                backgroundColor:
                    theme.palette.mode === 'dark'
                        ? theme.palette.primary.light
                        : theme.palette.primary.main,
            },
            '&:hover': {
                color:
                    theme.palette.mode === 'dark'
                        ? theme.palette.primary.light
                        : theme.palette.primary.main,
                background:
                    theme.palette.mode === 'light'
                        ? `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.045)} 0%, ${alpha(theme.palette.primary.main, 0.115)} 100%)`
                        : `linear-gradient(90deg, ${alpha(theme.palette.primary.light, 0.08)} 0%, ${alpha(theme.palette.primary.light, 0.17)} 100%)`,
                boxShadow: `inset 0 0 0 1px ${alpha(
                    theme.palette.mode === 'dark'
                        ? theme.palette.primary.light
                        : theme.palette.primary.main,
                    theme.palette.mode === 'dark' ? 0.15 : 0.085,
                )}`,
                '.MuiListItemIcon-root': {
                    color:
                        theme.palette.mode === 'dark'
                            ? theme.palette.primary.light
                            : theme.palette.primary.main,
                    backgroundColor: alpha(
                        theme.palette.mode === 'dark'
                            ? theme.palette.primary.light
                            : theme.palette.primary.main,
                        theme.palette.mode === 'dark' ? 0.13 : 0.12,
                    ),
                    transform: 'none',
                },
            },
        },

        // Keep the selected sidebar item stable on hover in light mode too.
        // The doubled parent selector intentionally wins over the global
        // MuiListItemButton .active:hover override.
        '&& .MuiListItemButton-root.active:hover': {
            color:
                theme.palette.mode === 'dark'
                    ? theme.palette.primary.light
                    : theme.palette.primary.main,
            backgroundColor: 'transparent',
            backgroundImage:
                theme.palette.mode === 'light'
                    ? `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.045)} 0%, ${alpha(theme.palette.primary.main, 0.115)} 100%)`
                    : `linear-gradient(90deg, ${alpha(theme.palette.primary.light, 0.08)} 0%, ${alpha(theme.palette.primary.light, 0.17)} 100%)`,
            boxShadow: `inset 0 0 0 1px ${alpha(
                theme.palette.mode === 'dark'
                    ? theme.palette.primary.light
                    : theme.palette.primary.main,
                theme.palette.mode === 'dark' ? 0.15 : 0.085,
            )}`,
            '.MuiTypography-root, .MuiListItemIcon-root, i': {
                color: 'inherit',
            },
            '.MuiListItemIcon-root': {
                color:
                    theme.palette.mode === 'dark'
                        ? theme.palette.primary.light
                        : theme.palette.primary.main,
                backgroundColor: alpha(
                    theme.palette.mode === 'dark'
                        ? theme.palette.primary.light
                        : theme.palette.primary.main,
                    theme.palette.mode === 'dark' ? 0.13 : 0.12,
                ),
                transform: 'none',
            },
        },
    })),

    UserArea: styled(Button)(({ theme }) => ({
        minHeight: 64,
        width: '100%',
        padding: theme.spacing(0.7, 0.8),
        borderRadius: 8,
        justifyContent: 'space-between',
        color: theme.palette.text.primary,
        border: `1px solid ${theme.palette.border.light}`,
        background: `linear-gradient(135deg, ${alpha(
            theme.palette.background.extraLight ?? theme.palette.background.paper,
            0.92,
        )} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
        boxShadow: `0 8px 22px ${alpha(
            theme.palette.common.black,
            theme.palette.mode === 'dark' ? 0.16 : 0.055,
        )}`,
        transition: theme.transitions.create(
            ['background-color', 'border-color', 'box-shadow', 'transform'],
            { duration: 150 },
        ),
        '&:hover': {
            transform: 'translateY(-1px)',
            backgroundColor: alpha(theme.palette.primary.main, 0.055),
            borderColor: alpha(theme.palette.primary.main, 0.22),
            boxShadow: `0 10px 26px ${alpha(
                theme.palette.common.black,
                theme.palette.mode === 'dark' ? 0.22 : 0.075,
            )}`,
        },
        '&.open': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            borderColor: alpha(theme.palette.primary.main, 0.28),
        },
        '& .MuiButton-endIcon': {
            margin: 0,
            marginInlineStart: theme.spacing(0.5),
        },
        '& .account-chevron': {
            width: 30,
            height: 30,
            flexShrink: 0,
            display: 'grid',
            placeItems: 'center',
            borderRadius: 6,
            color: theme.palette.text.light,
            backgroundColor: alpha(theme.palette.text.secondary, 0.055),
            border: `1px solid ${theme.palette.border.light}`,
            transition: theme.transitions.create(['color', 'transform'], {
                duration: 150,
            }),
            '& svg': {
                fontSize: 17,
            },
        },
        '&.open .account-chevron': {
            color: theme.palette.primary.main,
            transform: 'rotate(-90deg)',
        },
    })),

    UserAreaDescription: styled(Box)(({ theme }) => ({
        marginInlineStart: theme.spacing(1.1),
        minWidth: 0,
        flex: 1,
        textAlign: 'start',
        '.MuiTypography-root': {
            textWrap: 'nowrap',
        },
    })),

    MenuItem: styled(MenuItem)(({ theme }) => ({
        backgroundColor: theme.palette.background.default,
        '&:hover': {
            backgroundColor: theme.palette.background.default,
        },
        padding: '20px',
        borderBottom: 1,
        borderColor: theme.palette.divider,
        cursor: 'default',
    })),
};

export { Styled };
